import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useExchangeRates } from '@/hooks/useExchangeRates';
import type { Currency } from '@/utils/currencyConverter';
import { updateExchangeRates } from '@/utils/currencyConverter';

interface ExchangeRatesContextType {
  rates: Record<Currency, number>;
  lastUpdated: Date | null;
  loading: boolean;
}

const ExchangeRatesContext = createContext<ExchangeRatesContextType | undefined>(undefined);

export const ExchangeRatesProvider = ({ children }: { children: ReactNode }) => {
  const exchangeRates = useExchangeRates();

  // Sync rates with the converter utility whenever they change
  useEffect(() => {
    if (!exchangeRates.loading) {
      updateExchangeRates(exchangeRates.rates);
    }
  }, [exchangeRates.rates, exchangeRates.loading]);

  return (
    <ExchangeRatesContext.Provider value={exchangeRates}>
      {children}
    </ExchangeRatesContext.Provider>
  );
};

export const useExchangeRatesContext = (): ExchangeRatesContextType => {
  const context = useContext(ExchangeRatesContext);
  if (!context) {
    // Return fallback values if used outside provider
    return {
      rates: { USD: 1, EUR: 0.92, GBP: 0.79, CAD: 1.36, AUD: 1.52 },
      lastUpdated: null,
      loading: false,
    };
  }
  return context;
};
